import { useTranslation } from 'react-i18next';

import { SectionHeader } from '@/components/SectionHeader';
import { genId } from '@/utils';
import { Button, Card } from '@arco-design/web-react';
import { IconPlus } from '@arco-design/web-react/icon';
import isEmpty from 'lodash/isEmpty';
import { useCallback, useState } from 'react';
import styled from 'styled-components';
import { GrayBox } from '../tools';
import { WorkloadComponent, WorkloadType } from '../types';
import { StorageItems } from './_components';
import { AddClaimTemplateModal } from './_components/add-claim-template-modal';
import { AddKeymapModal } from './_components/add-keymap-modal';
import { AddVolumeModal } from './_components/add-volume-modal';
import {
  isVolumeClaimTemplateItem,
  isVolumeKeymapItem,
  isVolumeStorageItem,
  useStorageSettingStore,
  VolumeItem,
} from './_hooks';

const { STATEFULSET } = WorkloadType;

interface StorageAddCardProps extends WorkloadComponent {
  cluster: string;
  namespace: string;
  /** StatefulSet 的 volumeClaimTemplates 字段 只能在创建时设置，不能在更新时修改。 */
  hiddenAddVolumeClaimTemplate?: boolean;
  /** 禁止编辑持久卷声明模版 */
  disableEditVolumeClaimTemplate?: boolean;
}

export function StorageAddCard({
  module,
  cluster,
  namespace,
  hiddenAddVolumeClaimTemplate,
  disableEditVolumeClaimTemplate,
}: StorageAddCardProps) {
  const { t } = useTranslation();
  const [claimTemplateModalVisible, setClaimTemplateModalVisible] = useState(false);
  const [volumeModalVisible, setVolumeModalVisible] = useState(false);
  const [keymapModalVisible, setKeymapModalVisible] = useState(false);
  const [editVolume, setEditVolume] = useState<VolumeItem | null>(null);
  const [editIndex, setEditIndex] = useState<number | null>(null);
  const { volumes, addVolume, updateVolume, removeVolume } = useStorageSettingStore();
  const setUpdateItem = useCallback((item: VolumeItem, index: number) => {
    setEditVolume(item);
    setEditIndex(index);
  }, []);
  const clearUpdateItem = useCallback(() => {
    setEditVolume(null);
    setEditIndex(null);
  }, []);

  // 点击编辑
  const handleEdit = (item: VolumeItem, index: number) => {
    setUpdateItem(item, index);

    // 打开对应的窗口
    if (isVolumeStorageItem(item)) {
      setVolumeModalVisible(true);
    }

    if (isVolumeKeymapItem(item)) {
      setKeymapModalVisible(true);
    }

    if (isVolumeClaimTemplateItem(item)) {
      setClaimTemplateModalVisible(true);
    }
  };

  // 点击删除
  const handleDelete = (item: VolumeItem) => {
    removeVolume(item);
  };

  // 关闭弹窗
  const handleCancel = () => {
    setVolumeModalVisible(false);
    setKeymapModalVisible(false);
    setClaimTemplateModalVisible(false);
    clearUpdateItem();
  };

  // 确认弹窗
  const handleOk = (values: VolumeItem) => {
    /** 新增 */
    if (isEmpty(editVolume)) {
      // 如果没有编辑的 volume，则新增
      if (!values.volumeName) {
        const volumeName = genId({ prefix: 'volume-' });
        addVolume({ ...values, volumeName });
      } else {
        addVolume(values);
      }
    } else {
      /** 编辑 */
      updateVolume(values, editIndex);
    }
    handleCancel();
  };

  return (
    <>
      <StorageItems
        items={volumes}
        onEdit={handleEdit}
        onDelete={handleDelete}
        disableEditVolumeClaimTemplate={disableEditVolumeClaimTemplate}
      />
      <DashedCard>
        {module === STATEFULSET && !hiddenAddVolumeClaimTemplate && (
          <Content>
            <SectionHeader
              inline
              title={t('components:add.persistent.volume.claim.template')}
              description={t(
                'components:add.persistent.volume.claim.template.to.mount.a.persistent.volume.for.each.pod.in.statefulset',
              )}
            />
            <Button
              icon={<IconPlus />}
              type="primary"
              onClick={() => setClaimTemplateModalVisible(true)}
            />
          </Content>
        )}
        <Content>
          <SectionHeader
            inline
            title={t('common:mount.volume')}
            description={t(
              'components:mount.persistent.ephemeral.or.hostpath.volumes.for.containers',
            )}
          />
          <Button icon={<IconPlus />} type="primary" onClick={() => setVolumeModalVisible(true)} />
        </Content>
        <Content>
          <SectionHeader
            inline
            title={t('components:mount.configmap.or.secret')}
            description={t('components:mount.configmap.or.secret.for.containers')}
          />
          <Button icon={<IconPlus />} type="primary" onClick={() => setKeymapModalVisible(true)} />
        </Content>
        <AddClaimTemplateModal
          module={module}
          cluster={cluster}
          open={claimTemplateModalVisible}
          onClose={handleCancel}
          onOk={handleOk}
          data={editVolume}
        />
        <AddVolumeModal
          module={module}
          cluster={cluster}
          namespace={namespace}
          open={volumeModalVisible}
          onClose={handleCancel}
          onOk={handleOk}
          data={editVolume}
        />
        <AddKeymapModal
          module={module}
          cluster={cluster}
          namespace={namespace}
          open={keymapModalVisible}
          onClose={handleCancel}
          onOk={handleOk}
          data={editVolume}
        />
      </DashedCard>
    </>
  );
}

/** ============================ 样式 ==================================== */
const DashedCard = styled(Card)`
  border-style: dashed;
  .arco-card-body {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }
`;

const Content = styled(GrayBox)`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;
